import { useCallback, useEffect, useRef, DependencyList } from 'react';

export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
  deps: DependencyList = [],
): T {
  // Store the latest callback in a ref so it's always current
  const callbackRef = useRef(callback);

  // Update the ref whenever callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback, ...deps]);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Create the debounced function (memoized, doesn't change between renders)
  const debouncedCallback = useCallback(
    // eslint-disable-next-line react-hooks/use-memo
    ((...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    }) as T,
    [delay], // Only recreate if delay changes
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
}
